import { useState } from 'preact/hooks';

import { t } from '../i18n';
import { nameFromEmail } from '../reporter';
import { Icon, initials } from './icons';

import type { Reporter } from '../types';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Who is giving feedback. Identities used before in this browser can be picked again; a new one
 * needs only an email address, and the name is filled in from it (the team can correct it later).
 */
export function IdentityForm({
  known,
  privacyUrl,
  onSave,
  onForget,
  onCancel,
}: {
  known: Reporter[];
  privacyUrl?: string;
  onSave: (reporter: Reporter) => void;
  onForget: (email: string) => void;
  onCancel?: () => void;
}) {
  const s = t();
  const [adding, setAdding] = useState(known.length === 0);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const suggested = EMAIL.test(email.trim()) ? nameFromEmail(email.trim()) : '';
  const shownName = nameTouched ? name : suggested;

  const privacy = (
    <p class="hfb-privacy">
      <Icon name="lock" />
      <span>
        {s.privacy}{' '}
        {privacyUrl && (
          <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
            {s.privacyLink}
          </a>
        )}
      </span>
    </p>
  );

  if (!adding) {
    return (
      <div class="hfb-identity">
        <div class="hfb-form">
          <h2 class="hfb-display">{s.whoTitle}</h2>
          <p class="hfb-muted">{s.pickWho}</p>
          <ul class="hfb-people">
            {known.map((r) => (
              <li key={r.email}>
                <button type="button" class="hfb-person" onClick={() => onSave(r)}>
                  <span class="hfb-avatar" aria-hidden="true">
                    {initials(r.name)}
                  </span>
                  <span class="hfb-person-text">
                    <span class="hfb-hint">{s.continueAs}</span>
                    <b>{r.name}</b>
                    <span class="hfb-muted">{r.email}</span>
                  </span>
                </button>
                <button
                  type="button"
                  class="hfb-icon-btn hfb-person-forget"
                  aria-label={`${s.forget}: ${r.name}`}
                  title={s.forget}
                  onClick={() => {
                    if (known.length === 1) setAdding(true);
                    onForget(r.email);
                  }}
                >
                  <Icon name="x" size={16} />
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            class="hfb-btn hfb-btn-outline hfb-btn-block"
            onClick={() => setAdding(true)}
          >
            {s.someoneElse}
          </button>
          {onCancel && (
            <button type="button" class="hfb-btn hfb-btn-ghost hfb-btn-block" onClick={onCancel}>
              {s.cancel}
            </button>
          )}
          <p class="hfb-hint">{s.savedLocally}</p>
        </div>
        {privacy}
      </div>
    );
  }

  return (
    <div class="hfb-identity">
      <form
        class="hfb-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          const value = email.trim().toLowerCase();
          if (!EMAIL.test(value)) {
            setEmailError(true);
            (
              event.currentTarget.querySelector('input[type=email]') as HTMLInputElement | null
            )?.focus();
            return;
          }
          onSave({ email: value, name: shownName.trim() || nameFromEmail(value) || value });
        }}
      >
        <h2 class="hfb-display">{s.whoTitle}</h2>
        <p class="hfb-muted">{s.whoIntro}</p>
        <label class="hfb-field">
          <span class="hfb-label">{s.workEmail}</span>
          <input
            class={`hfb-input${emailError ? ' is-invalid' : ''}`}
            type="email"
            value={email}
            aria-invalid={emailError}
            aria-describedby={emailError ? 'hfb-email-error' : undefined}
            onInput={(e) => {
              setEmail(e.currentTarget.value);
              if (emailError) setEmailError(false);
            }}
            autoComplete="email"
            autoFocus
          />
          {emailError && (
            <span class="hfb-field-error" id="hfb-email-error">
              {s.emailInvalid}
            </span>
          )}
        </label>
        <label class="hfb-field">
          <span class="hfb-label-row">
            <span class="hfb-label">{s.name}</span>
            <span class="hfb-hint">{s.optional}</span>
          </span>
          <input
            class="hfb-input"
            value={shownName}
            placeholder={s.namePlaceholder}
            onInput={(e) => {
              setNameTouched(true);
              setName(e.currentTarget.value);
            }}
            autoComplete="name"
          />
          <span class="hfb-hint">{s.nameHint}</span>
        </label>
        <button type="submit" class="hfb-btn hfb-btn-primary hfb-btn-block">
          {s.continue}
        </button>
        {(known.length > 0 || onCancel) && (
          <button
            type="button"
            class="hfb-btn hfb-btn-ghost hfb-btn-block"
            onClick={() => (known.length > 0 ? setAdding(false) : onCancel?.())}
          >
            {s.cancel}
          </button>
        )}
        <p class="hfb-hint">{s.savedLocally}</p>
      </form>
      {privacy}
    </div>
  );
}
